import { DriveStep } from 'driver.js';

export type TourType = 'os' | 'cursus' | 'cours' | 'marketing' | 'users' | 'organizations' | 'enrollment' | 'monof';

export interface TourConfig {
    id: TourType;
    name: string;
    description: string;
    requiredPath?: string; // Path to navigate to before starting tour
    steps: DriveStep[];
}

// OS Interface Tour
export const osTour: TourConfig = {
    id: 'os',
    name: 'Interface OS',
    description: 'Découvrez l\'interface d\'administration',
    steps: [
        {
            element: '#admin-os-layout',
            popover: {
                title: 'Bienvenue sur Edu-JALM',
                description: 'Ceci est votre interface d\'administration. Elle fonctionne comme un système d\'exploitation de bureau.',
                side: 'bottom',
                align: 'center'
            }
        },
        {
            element: '#desktop-area',
            popover: {
                title: 'Bureau',
                description: 'Ici, vous trouverez les icônes pour accéder aux différentes applications.',
                side: 'bottom',
                align: 'start'
            }
        },
        {
            element: '#icon-cursus',
            popover: {
                title: 'Gestion des Parcours',
                description: 'Créez et gérez vos parcours de formation, blocs de compétences et structure globale.',
                side: 'right',
                align: 'center'
            }
        },
        {
            element: '#icon-courses',
            popover: {
                title: 'Liste des Cours',
                description: 'Accédez à tous vos cours, éditez leur contenu (leçons, quiz, vidéos) et gérez les sections.',
                side: 'right',
                align: 'center'
            }
        },
        {
            element: '#icon-marketing',
            popover: {
                title: 'Marketing & Offres',
                description: 'Configurez vos offres d\'abonnement, prix et pages de vente.',
                side: 'right',
                align: 'center'
            }
        },
        {
            element: '#icon-users',
            popover: {
                title: 'Utilisateurs',
                description: 'Gérez les comptes apprenants, formateurs et administrateurs.',
                side: 'right',
                align: 'center'
            }
        },
        {
            element: '#icon-organizations',
            popover: {
                title: 'Gestion des Organismes',
                description: 'Créez et gérez les organismes de formation (OF), consultez leurs statistiques et assignez-leur des offres.',
                side: 'right',
                align: 'center'
            }
        },
        {
            element: '#icon-enrollment',
            popover: {
                title: 'Inscriptions aux Parcours',
                description: 'Gérez les inscriptions des apprenants : création, modification, report de dates et suivi financier.',
                side: 'right',
                align: 'center'
            }
        },
        {
            element: '#icon-learner-space',
            popover: {
                title: 'Espace Apprenant',
                description: 'Accédez à l\'interface apprenant pour tester l\'expérience utilisateur en temps réel.',
                side: 'right',
                align: 'center'
            }
        },
        {
            element: '#icon-settings',
            popover: {
                title: 'Paramètres Système',
                description: 'Configurez la plateforme : apparence, applications visibles, langue et préférences personnelles.',
                side: 'right',
                align: 'center'
            }
        },
        {
            element: '#taskbar',
            popover: {
                title: 'Barre des tâches',
                description: 'Accès rapide à toutes les applications, bouton d\'aide et déconnexion.',
                side: 'top',
                align: 'center'
            }
        },
        {
            element: '#dashboard-widget',
            popover: {
                title: 'Widgets',
                description: 'Gardez un œil sur les statistiques importantes de votre plateforme.',
                side: 'left',
                align: 'start'
            }
        }
    ]
};

// Cursus/Parcours Tour
export const cursusTour: TourConfig = {
    id: 'cursus',
    name: 'Gestion des Parcours',
    description: 'Apprenez à gérer les parcours de formation',
    requiredPath: '/parcours',
    steps: [
        {
            popover: {
                title: 'Gestion des Parcours',
                description: 'Cette application vous permet de créer et gérer la structure complète de vos formations : Parcours, Blocs et Catégories.'
            }
        },
        {
            element: '[data-tour="add-parcours-btn"]',
            popover: {
                title: 'Créer un Parcours',
                description: 'Cliquez ici pour créer un nouveau parcours de formation. Un parcours est le niveau le plus élevé de votre structure.',
                side: 'bottom',
                align: 'start'
            }
        },
        {
            element: '[data-tour="parcours-list"]',
            popover: {
                title: 'Liste des Parcours',
                description: 'Visualisez tous vos parcours existants. Cliquez sur un parcours pour voir ses blocs et catégories.',
                side: 'right',
                align: 'start'
            }
        },
        {
            element: '[data-tour="bloc-section"]',
            popover: {
                title: 'Blocs de Compétences',
                description: 'Les blocs regroupent des catégories de cours. Ajoutez des blocs pour organiser votre parcours.',
                side: 'left',
                align: 'start'
            }
        }
    ]
};

// Cours Tour
export const coursTour: TourConfig = {
    id: 'cours',
    name: 'Gestion des Cours',
    description: 'Créez et éditez vos cours',
    requiredPath: '/courses',
    steps: [
        {
            popover: {
                title: 'Gestion des Cours',
                description: 'Créez et gérez le contenu de vos cours : sections, leçons, quiz et ressources.',
            }
        },
        {
            element: '[data-tour="course-list"]',
            popover: {
                title: 'Liste des Cours',
                description: 'Tous vos cours sont listés ici. Cliquez sur un cours pour l\'éditer.',
                side: 'right',
                align: 'start'
            }
        },
        {
            element: '[data-tour="add-course-btn"]',
            popover: {
                title: 'Créer un Cours',
                description: 'Créez un nouveau cours en cliquant ici.',
                side: 'bottom',
                align: 'start'
            }
        },
        {
            element: '[data-tour="course-designer"]',
            popover: {
                title: 'Designer de Cours',
                description: 'Utilisez le designer pour structurer votre cours avec des sections et des leçons. Glissez-déposez pour réorganiser.',
                side: 'left',
                align: 'start'
            }
        }
    ]
};

// Marketing Tour
export const marketingTour: TourConfig = {
    id: 'marketing',
    name: 'Marketing & Offres',
    description: 'Gérez vos offres commerciales',
    requiredPath: '/marketing',
    steps: [
        {
            popover: {
                title: 'Marketing & Offres',
                description: 'Créez et gérez vos offres commerciales : prix, réductions, abonnements.',
            }
        },
        {
            element: '[data-tour="add-offer-btn"]',
            popover: {
                title: 'Créer une Offre',
                description: 'Créez une nouvelle offre commerciale avec ses tarifs et parcours associés.',
                side: 'bottom',
                align: 'start'
            }
        },
        {
            element: '[data-tour="offers-table"]',
            popover: {
                title: 'Liste des Offres',
                description: 'Visualisez toutes vos offres. Modifiez les prix, activez/désactivez les offres.',
                side: 'top',
                align: 'start'
            }
        },
        {
            element: '[data-tour="assign-offer-btn"]',
            popover: {
                title: 'Assigner aux Organismes',
                description: 'Assignez vos offres à des organismes de formation spécifiques.',
                side: 'left',
                align: 'center'
            }
        }
    ]
};

// Users Tour
export const usersTour: TourConfig = {
    id: 'users',
    name: 'Gestion des Utilisateurs',
    description: 'Gérez les comptes utilisateurs',
    requiredPath: '/users',
    steps: [
        {
            popover: {
                title: 'Gestion des Utilisateurs',
                description: 'Créez et gérez tous les comptes : apprenants, formateurs, administrateurs.',
            }
        },
        {
            element: '[data-tour="add-user-btn"]',
            popover: {
                title: 'Créer un Utilisateur',
                description: 'Ajoutez un nouveau compte utilisateur avec son rôle et ses permissions.',
                side: 'bottom',
                align: 'start'
            }
        },
        {
            element: '[data-tour="users-table"]',
            popover: {
                title: 'Liste des Utilisateurs',
                description: 'Visualisez tous les utilisateurs. Filtrez par rôle, modifiez ou supprimez des comptes.',
                side: 'top',
                align: 'start'
            }
        },
        {
            element: '[data-tour="role-filter"]',
            popover: {
                title: 'Filtrer par Rôle',
                description: 'Filtrez rapidement les utilisateurs par leur rôle : super-admin, OF-admin, formateur, apprenant.',
                side: 'left',
                align: 'center'
            }
        }
    ]
};

// Organizations Tour
export const organizationsTour: TourConfig = {
    id: 'organizations',
    name: 'Gestion des Organismes',
    description: 'Gérez les organismes de formation',
    requiredPath: '/organizations',
    steps: [
        {
            popover: {
                title: 'Gestion des Organismes',
                description: 'Créez et gérez les organismes de formation (OF), consultez leurs statistiques.',
            }
        },
        {
            element: '[data-tour="add-org-btn"]',
            popover: {
                title: 'Créer un Organisme',
                description: 'Créez un nouvel organisme de formation avec ses informations.',
                side: 'bottom',
                align: 'start'
            }
        },
        {
            element: '[data-tour="org-list"]',
            popover: {
                title: 'Liste des Organismes',
                description: 'Visualisez tous les organismes. Cliquez pour voir les détails et statistiques.',
                side: 'right',
                align: 'start'
            }
        },
        {
            element: '[data-tour="org-stats"]',
            popover: {
                title: 'Statistiques',
                description: 'Consultez les statistiques de chaque organisme : nombre d\'apprenants, inscriptions, etc.',
                side: 'left',
                align: 'center'
            }
        }
    ]
};

// Enrollment Tour
export const enrollmentTour: TourConfig = {
    id: 'enrollment',
    name: 'Gestion des Inscriptions',
    description: 'Gérez les inscriptions aux parcours',
    requiredPath: '/enrollment',
    steps: [
        {
            popover: {
                title: 'Gestion des Inscriptions',
                description: 'Créez et gérez les inscriptions des apprenants aux parcours : dates, paiements, statuts.',
            }
        },
        {
            element: '[data-tour="add-enrollment-btn"]',
            popover: {
                title: 'Nouvelle Inscription',
                description: 'Créez une inscription en sélectionnant l\'apprenant, le parcours et les dates.',
                side: 'bottom',
                align: 'start'
            }
        },
        {
            element: '[data-tour="enrollment-table"]',
            popover: {
                title: 'Liste des Inscriptions',
                description: 'Visualisez toutes les inscriptions. Filtrez par statut, apprenant ou parcours.',
                side: 'top',
                align: 'start'
            }
        },
        {
            element: '[data-tour="enrollment-actions"]',
            popover: {
                title: 'Actions',
                description: 'Modifiez, reportez ou annulez des inscriptions. Consultez les détails financiers.',
                side: 'left',
                align: 'center'
            }
        }
    ]
};

// Mon OF Tour
export const monofTour: TourConfig = {
    id: 'monof',
    name: 'Mon Organisme de Formation',
    description: 'Gérez les paramètres de votre organisme',
    requiredPath: '/mon-of',
    steps: [
        {
            popover: {
                title: 'Bienvenue sur Mon OF',
                description: 'Cette application vous permet de personnaliser votre organisme de formation : logo, couleurs et informations.',
                side: 'bottom',
                align: 'center'
            }
        },
        {
            element: '.MuiCard-root:first-of-type',
            popover: {
                title: 'Logo de l\'organisme',
                description: 'Téléversez le logo de votre organisme. Il apparaîtra dans l\'en-tête de l\'OS et dans l\'espace apprenant.',
                side: 'right',
                align: 'start'
            }
        },
        {
            element: 'input[type="color"][name="primaryColor"]',
            popover: {
                title: 'Couleur principale',
                description: 'Choisissez la couleur principale de votre thème. Elle sera utilisée pour les en-têtes et éléments importants.',
                side: 'bottom',
                align: 'center'
            }
        },
        {
            element: 'input[type="color"][name="secondaryColor"]',
            popover: {
                title: 'Couleur secondaire',
                description: 'Définissez la couleur secondaire pour les accents et éléments de navigation.',
                side: 'bottom',
                align: 'center'
            }
        },
        {
            element: '.MuiCard-root:nth-of-type(2)',
            popover: {
                title: 'Aperçu Espace Apprenant',
                description: 'Visualisez en temps réel comment votre logo et vos couleurs apparaîtront dans l\'interface des apprenants.',
                side: 'left',
                align: 'center'
            }
        },
        {
            element: 'button:has-text("Sauvegarder les modifications")',
            popover: {
                title: 'Sauvegarder',
                description: 'N\'oubliez pas de sauvegarder vos modifications pour les appliquer à votre organisme.',
                side: 'top',
                align: 'center'
            }
        }
    ]
};

export const allTours: TourConfig[] = [
    osTour,
    cursusTour,
    coursTour,
    marketingTour,
    usersTour,
    organizationsTour,
    enrollmentTour,
    monofTour
];

export const getTourById = (id: TourType): TourConfig | undefined => {
    return allTours.find(tour => tour.id === id);
};
